import { CollectionConfig } from "@rebasepro/types";
import companiesCollection from "./companies";

/**
 * Company claims — who came to manage a company, how, and who let them.
 *
 * One row per person per company, written in three ways and never by the
 * person themselves:
 *
 *  - **`signup`**, by a trigger on `companies` (drizzle/0061), the moment a
 *    signed-in user creates a company through `/registro/empresa`. It is the
 *    only record of *who created* a company row, and it is what the RLS path
 *    into `company_members` checks — `claim_unclaimed_company` admits a first
 *    member only when this row names them and is less than an hour old.
 *  - **`domain`**, by `functions/claim-company`, when the account's confirmed
 *    address is at the domain the record already carries.
 *  - **`review`**, by the same function, when nothing on file can prove it:
 *    no usable domain on the record, or an address at a different one. A
 *    person approves or refuses it from the company's cover
 *    (`functions/decide-company-claim`).
 *
 * The claimant reads their own rows — that is how the app tells them their
 * request is waiting — and nobody else but the team reads any. No INSERT or
 * UPDATE policy exists for anyone but admins: a claim a user could write
 * themselves would be a claim that proves nothing.
 *
 * `email` is the account's address as the auth table held it when the claim
 * was made, read on the server, never taken from the request. Where an address
 * came from is never hidden (PRODUCT.md), and this one came from the account.
 */
const companyClaimsCollection: CollectionConfig = {
    name: "Solicitudes de ficha",
    singularName: "Solicitud de ficha",
    slug: "company-claims",
    table: "company_claims",
    // Own the RLS outright, as company_leads does: the generator would add
    // `*_default_admin_*` policies on every boot otherwise.
    disableDefaultPolicies: true,
    securityRules: [
        {
            name: "claimant_read_own",
            operation: "select",
            using: "auth.uid() = auth_user_id"
        },
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        company_id: {
            name: "Empresa",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                localKey: "company_id"
            },
            validation: { required: true }
        },
        auth_user_id: {
            name: "Cuenta",
            type: "string",
            userSelect: true,
            validation: { required: true },
            admin: { readOnly: true }
        },
        email: {
            name: "Correo de la cuenta",
            type: "string",
            description: "El de la cuenta que la pidió, leído de la cuenta, no escrito por nadie.",
            admin: { readOnly: true }
        },
        contact_name: {
            name: "Nombre",
            type: "string"
        },
        contact_phone: {
            name: "Teléfono",
            type: "string"
        },
        requested_services: {
            name: "Servicios que pidió",
            type: "array",
            columnType: "text[]",
            of: {
                name: "Servicio",
                type: "string",
                enum: [
                    { id: "headhunting", label: "Selección y contratación de talento", color: "green" },
                    { id: "attraction_optimization", label: "Atracción y optimización de procesos", color: "blue" }
                ]
            }
        },
        privacy_accepted: {
            name: "Aceptó la privacidad",
            type: "boolean"
        },
        company_domain: {
            name: "Dominio de la ficha entonces",
            type: "string",
            description: "El dominio que tenía la ficha cuando llegó la solicitud: contra esto se comparó el correo.",
            admin: { readOnly: true }
        },
        method: {
            name: "Cómo",
            type: "string",
            enum: [
                { id: "signup", label: "La registró desde la web", color: "green" },
                { id: "domain", label: "Correo del dominio de la ficha", color: "teal" },
                { id: "review", label: "La revisa el equipo", color: "orange" }
            ],
            validation: { required: true },
            admin: { readOnly: true }
        },
        status: {
            name: "Estado",
            type: "string",
            enum: [
                { id: "pending", label: "Pendiente", color: "orange" },
                { id: "approved", label: "Aprobada", color: "green" },
                { id: "refused", label: "Rechazada", color: "gray" }
            ],
            validation: { required: true }
        },
        decided_by: {
            name: "Decidió",
            type: "string",
            admin: { readOnly: true }
        },
        decided_at: {
            name: "Decidida el",
            type: "date",
            admin: { readOnly: true }
        },
        welcome_sent_at: {
            name: "Bienvenida enviada el",
            type: "date",
            description: "Cuando el servidor de correo aceptó el correo de bienvenida. Vacío: no se ha enviado.",
            admin: { readOnly: true }
        },
        created_at: {
            name: "Pedida el",
            type: "date",
            autoValue: "on_create"
        }
    },
    admin: {
        group: "Empresas",
        icon: "KeyRound",
        // Worked from the company's cover, where "who is asking" sits next to
        // "what we know about this company". As a list it is a table of
        // strangers' addresses with nothing to judge them against.
        hideFromNavigation: true,
        sort: ["created_at", "desc"],
        listProperties: ["company_id", "email", "method", "status", "created_at"],
        previewProperties: ["email", "status"]
    }
};

export default companyClaimsCollection;
